/**
 * What the map reads from hg-order. Every key starts with `MAP_KEY`.
 *
 * - Who is at work and where: read when the map opens, then moved by the
 *   points of the staff stream (`lib/positions.ts`), and read again when
 *   somebody comes to work or leaves it (`./live.ts`).
 * - A road is read when somebody asks to see it, once: hg-order writes every
 *   such read to its log, and the points that follow come over the stream.
 */
import { keepPreviousData, queryOptions } from "@tanstack/react-query";

import { orderApi, unwrap } from "@/shared/api/clients";
import { instantIn, MARKET_TIME_ZONE } from "@/shared/lib/format";

import { DEFAULT_SPAN, type RoadSpan } from "./types";

export const MAP_KEY = ["map"] as const;

export function positionsKey(areaId: string | undefined) {
  return [...MAP_KEY, "positions", areaId ?? null] as const;
}

/** Everybody at work now in the caller's areas, or one of them, each at their last point. */
export function positionsQuery(areaId: string | undefined) {
  return queryOptions({
    queryKey: positionsKey(areaId),
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        orderApi.GET("/v2/admin/specialists/positions", {
          params: { query: areaId === undefined ? {} : { area_id: areaId } },
          signal,
        }),
      );
      return data;
    },
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

export function trackKey(userId: string, span: RoadSpan) {
  return [...MAP_KEY, "track", userId, span] as const;
}

/** What `GET …/track` is asked for a span: nothing for the service's own two hours. */
function spanQuery(span: RoadSpan): { from?: string; to?: string } {
  if (typeof span === "number") {
    // The service's own span needs no dates; a longer one starts that long before now.
    return span === DEFAULT_SPAN
      ? {}
      : { from: new Date(Date.now() - span * 3_600_000).toISOString() };
  }
  // A span of one's own: its moments are the market's clock's.
  const from = instantIn(span.from, MARKET_TIME_ZONE);
  const to = span.to === undefined ? null : instantIn(span.to, MARKET_TIME_ZONE);
  return { ...(from === null ? {} : { from }), ...(to === null ? {} : { to }) };
}

/** A specialist's road through the last hours, or through a span of one's own. */
export function trackQuery(userId: string, span: RoadSpan) {
  return queryOptions({
    queryKey: trackKey(userId, span),
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        orderApi.GET("/v2/admin/specialists/{user_id}/track", {
          params: { path: { user_id: userId }, query: spanQuery(span) },
          signal,
        }),
      );
      return data;
    },
    // Read once: what follows is added from the stream, and every read is logged.
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });
}

/** The road of one visit. */
export function visitTrackQuery(visitId: string) {
  return queryOptions({
    queryKey: [...MAP_KEY, "visit", visitId] as const,
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        orderApi.GET("/v2/admin/visits/{visit_id}/track", {
          params: { path: { visit_id: visitId } },
          signal,
        }),
      );
      return data;
    },
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });
}
